import { monthNames, announcableCardinalDays } from './constants.js';
import { convertToPositiveNumberIfPossible } from './numberHelpers.js';
import type { DSControlledDateTimePickerInternalsT } from '../sharedTypes.js';
/* eslint-disable max-lines */
export const prependStringWithPlaceHolders = (val: string, finalLength: number, placeholder = '0'): string => {
  if (val.length === finalLength) return val;
  const stringAsAnArray = val.split('').reverse();
  return new Array(finalLength)
    .fill(placeholder)
    .map((_c, i) => (stringAsAnArray[i] ? stringAsAnArray[i] : placeholder))
    .reverse()
    .join('');
};
export const fillStringWithPlaceHolders = (val: string, finalLength: number, placeholder = '_'): string => {
  if (val.length === finalLength) return val;
  const stringAsAnArray = val.split('');
  return new Array(finalLength)
    .fill(placeholder)
    .map((_c, i) => (stringAsAnArray[i] ? stringAsAnArray[i] : placeholder))
    .join('');
};

export const getFormattedDateString = ({ month, day, year }: { month: string; day: string; year: string }): string => {
  const finalMonth = fillStringWithPlaceHolders(month, 2);
  const finalDay = fillStringWithPlaceHolders(day, 2);
  const finalYear = fillStringWithPlaceHolders(year, 4);
  return `${finalMonth}/${finalDay}/${finalYear}`;
};
export const dateTimeToDate = (dateTime: string): string => dateTime?.split?.(' ')?.[0];
export const deconstructValuesFromDateString = (dateString: string): { month: string; day: string; year: string } => {
  const [dirtyMonth = '', dirtyDay = '', dirtyYear = ''] = dateString.split('/');
  const replaceAllRegexp = /_/g;
  return {
    month: (dirtyMonth || '').replace(replaceAllRegexp, ''),
    day: (dirtyDay || '').replace(replaceAllRegexp, ''),
    year: (dirtyYear || '').replace(replaceAllRegexp, ''),
  };
};
export const deconstructValuesFromTimeString = (
  timeString: string,
): { hours: string; minutes: string; meridiem: string } => {
  const [dirtyHour = '', dirtyMintuesAndMeridiem = ''] = timeString.split(':');
  const [dirtyMinutes = '', dirtyMeridiem = ''] = dirtyMintuesAndMeridiem.split(' ');
  const replaceAllRegexp = /_/g;
  return {
    hours: (dirtyHour || '').replace(replaceAllRegexp, ''),
    minutes: (dirtyMinutes || '').replace(replaceAllRegexp, ''),
    meridiem: (dirtyMeridiem || '').replace(replaceAllRegexp, ''),
  };
};
// https://regex101.com/r/U5r5lh/1/
const dateRegExp = /^[0-9_][0-9_]\/[0-9_][0-9_]\/[0-9_]{4}$/;
export const isValidDateString = (testString: string): boolean => {
  if (testString === '') return true;
  const isValidDateRegexp = dateRegExp.test(testString);
  if (!isValidDateRegexp) return false;
  const { month, day, year } = deconstructValuesFromDateString(testString);
  if (Number.parseInt(month, 10) > 12 || Number.parseInt(day, 10) > 31 || Number.parseInt(year, 10) > 9999)
    return false;

  return true;
};

export const getFormattedTimeString = ({
  hours,
  minutes,
  // seconds,
  meridiem,
}: {
  hours: string;
  minutes: string;
  // seconds:string;
  meridiem: string;
}): string => {
  const finalHour = fillStringWithPlaceHolders(hours, 2);
  const finalMinutes = fillStringWithPlaceHolders(minutes, 2);
  // const finalSeconds = fillStringWithPlaceHolders(seconds, 2); // <-- seconds format
  const finalMeridiem = fillStringWithPlaceHolders(meridiem, 2);
  // return `${finalHour}:${finalMinutes}:${finalSeconds} ${finalMeridiem}`;// <-- seconds format
  return `${finalHour}:${finalMinutes} ${finalMeridiem}`;
};
export const dateTimeToTime = (dateTime: string): string => {
  const [
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    date,
    // timeNumbers = '__:__:__', // <-- seconds format
    timeNumbers = '__:__',
    timeMeridiem = '__',
  ] = dateTime.split(' ');
  return `${timeNumbers} ${timeMeridiem}`;
};
export const deconstructTimeValues = (
  timeString: string,
): {
  hours: string;
  minutes: string;
  meridiem: string;
} => {
  const [dirtyTimes = '', dirtyMeridiem] = timeString.split(' '); // <--['__:__','__']
  const [dirtyHours, dirtyMinutes] = dirtyTimes.split(':'); // <--['__','__']
  const regex = /_/g; // Note the 'g' flag, which matches all occurrences of the expression
  const newValues = {
    hours: (dirtyHours || '').replace(regex, ''),
    minutes: (dirtyMinutes || '').replace(regex, ''),
    meridiem: (dirtyMeridiem || '').replace(regex, ''),
  };
  return newValues;
};

// https://regex101.com/r/jeBmNT/1
const timeRegExp = /^[0-9_][0-9_]:[0-9_][0-9_]\s[aApP_][mM_]$/;
export const isValidTimeString = (testString: string): boolean => {
  if (testString === '') return true;
  const isValidTimeRegexp = timeRegExp.test(testString);
  if (!isValidTimeRegexp) return false;
  const { hours, minutes } = deconstructTimeValues(testString);
  if (Number.parseInt(hours, 10) > 12 || Number.parseInt(minutes, 10) > 60) return false;

  return true;
};

export const isIncompleteString = (testString: string, placeholder = '_'): boolean =>
  testString === '' || testString.includes(placeholder);

export const isEmptyDateStringMask = (testString: string): boolean => testString === '__/__/____';
export const isEmptyTimeStringMask = (testString: string): boolean => testString === '__:__ __';
export const isEmptyDateTimeStringMask = (testString: string): boolean => testString === '__/__/____ __:__ __';

export const getAriaDayFromDateString = (dateString: string): string => {
  const { month: monthString, day: dayString, year: yearString } = deconstructValuesFromDateString(dateString);
  const monthNum = convertToPositiveNumberIfPossible(monthString);
  const dayNum = convertToPositiveNumberIfPossible(dayString) as DSControlledDateTimePickerInternalsT.DateDay | -1;
  const yearNum = convertToPositiveNumberIfPossible(yearString);
  if (monthNum === -1 || dayNum === -1 || yearNum === -1) return '';
  return `${monthNames[monthNum - 1]} ${announcableCardinalDays[dayNum]} ${yearNum}`;
};
